import {Injectable, signal} from '@angular/core';
import {finalize, first, map} from 'rxjs/operators';
import {MultiSite, SiteExisting} from '../types/site.type';
import {SettingsApiService} from './api/settings-api.service';
import {AccountService} from './account.service';
import {IUsageStatsExportCSVRequest} from '../common/models/interfaces';
import {PaymentStep, ProductType, StripeProduct} from '../types/payment.type';
import {MCActionsService} from './mc-base/mc-actions-service';
import {Tunings} from '../types/tunings';
import {AutoUnsubscribe} from '../decorators/auto-unsubscribe.decorator';
import {UserInfoQuery} from '../common/elf-store/user-info/user-info.query';
import {UserInfoState} from '../common/elf-store/user-info/user-info.type';
import {BehaviorSubject, Subscription, tap} from 'rxjs';
import {SitesManager} from '../common/elf-store/sites/sites.manager';
import {SitesQuery} from '../common/elf-store/sites/sites.query';
import {PaymentDataService} from './payment-data.service';
import {MySitesApiService} from './api/my-sites-api.service';
import {GoogleTagManagerService} from 'angular-google-tag-manager';
import {TrackingService} from './tracking.service';
import {EventEmitterService} from './event-emitter.service';
import {AuthService} from '../auth/shared/services/auth.service';
import {ToastrMessagesProvider} from './toastr-messages.service';
import {TuningsManager} from '../common/elf-store/tunings/tunings.manager';
import {TuningsQuery} from '../common/elf-store/tunings/tunings.query';
import {
  SiteSearchEntryDto,
  SiteSearchResultDto,
  SubscriptionBillingCycleType,
  UwV2DashboardProductsUsageStatsExportCsvListData,
  UwV2DashboardProductsUsageStatsListParams,
  UwV2SitesSearchListParams,
} from '../../userway/sdk/UserWayApiClient';

export enum TrialStatus {
  TrialApplicable = 'trialApplicable',
  TrialHasBeenUsed = 'trialHasBeenUsed',
}

@Injectable({
  providedIn: 'root',
})
@AutoUnsubscribe()
export class SiteDetailsService extends MCActionsService {
  accountInfo: UserInfoState;
  additionalSites: SiteSearchEntryDto[][] = [];
  tunings: Partial<Tunings>;
  currentSite: SiteSearchEntryDto;
  /* eslint-disable local/no-subscription-variables */
  tuningsSubscription: Subscription;
  currentSiteSubscription: Subscription;
  calculatedResponseSubscription: Subscription;
  selectedTab$: BehaviorSubject<{
    id: string;
    index: number;
  }> = new BehaviorSubject({
    index: 0,
    id: 'overview',
  });
  showPayment = signal(false);
  isImgAltsUpgrade = signal(false);

  constructor(
    private mySitesApiService: MySitesApiService,
    public eventEmitterService: EventEmitterService,
    private settingsApiService: SettingsApiService,
    private accountService: AccountService,
    private sitesManager: SitesManager,
    private tuningsManager: TuningsManager,
    private sitesQuery: SitesQuery,
    private tuningsQuery: TuningsQuery,
    private paymentDataService: PaymentDataService,
    private authService: AuthService,
    userInfoQuery: UserInfoQuery,
    private readonly gtmService: GoogleTagManagerService,
    private readonly trackingService: TrackingService,
    private toastr: ToastrMessagesProvider,
  ) {
    super(userInfoQuery);
    this.initialized$.next(true);
    this.tuningsSubscription = this.tuningsQuery.tunings$.subscribe(tunings => (this.tunings = tunings));
    this.currentSiteSubscription = this.sitesQuery.currentSite$.subscribe(site => (this.currentSite = site));
  }

  mcOnLogin() {
    this.accountInfo = this.accountService.getInfoFromJWT();
    this.getInfoWidget();
  }

  mcOnLogout() {
    this.resetAllData();
  }

  resetAdditionalSitesList() {
    this.additionalSites = [];
  }

  getLoadedSite$() {
    return this.sitesQuery.getLoadedSite$();
  }

  getSiteByUrlAndInitTunings(params: {searchValue: string; siteId?: number; onSuccessCallback?: () => void}): void {
    this.sitesManager.setSiteLoading(true);
    this.sitesManager.setSiteNotFound(false);
    const requestData: UwV2SitesSearchListParams = {s: params.searchValue, p: 1, q: 1};

    if (params?.siteId) {
      requestData.siteIds = params.siteId.toString();
    }

    this.mySitesApiService
      .mySites(requestData)
      .pipe(finalize(() => this.sitesManager.setSiteLoading(false)))
      .subscribe((response: SiteSearchResultDto) => {
        if (response.sites[0]) {
          this.sitesManager.setCurrentSite(response.sites[0]);
          this.initTunings();

          if (typeof params?.onSuccessCallback === 'function') {
            params.onSuccessCallback();
          }
        } else {
          this.sitesManager.setSiteNotFound(true);
        }
      });
  }

  initTunings(): void {
    this.tuningsManager.reset();
    this.tuningsManager.setLoading(true);
    this.settingsApiService
      .getSettings(
        {origin: this.currentSite?.url, ignore: true},
        this.authService.teamAccountCode || this.accountInfo.targetAccountCode,
      )
      .pipe(
        finalize(() => {
          this.tuningsManager.setLoading(false);
        }),
      )
      .subscribe({
        next: data => {
          this.sitesManager.setSiteTunings(data.data);
          this.tuningsManager.setTunings({...this.tunings, ...data.data.tunings});
          this.tuningsManager.setUpdatedTunings({...this.tunings, ...data.data.tunings});
          this.tuningsManager.setTuningsFetched(true);
        },
      });
  }

  updateTunings(tuningItem: Partial<Tunings>) {
    this.sitesManager.setSiteTunings({...this.tunings, tunings: tuningItem});
  }

  getInfoWidget(): void {
    this.sitesManager.setSitesCount(this.userInfo.sitesCount);
    const isTrialApplicable = this.userInfo.isTrialApplicable;
    this.sitesManager.setTrial(isTrialApplicable ? TrialStatus.TrialApplicable : TrialStatus.TrialHasBeenUsed);
  }

  getUsageStats(settings: UwV2DashboardProductsUsageStatsListParams) {
    if (!settings?.siteId) {
      return;
    }

    return this.settingsApiService.getUsageStats(settings).pipe(
      map(response => {
        return response.payload;
      }),
    );
  }

  upgradeStatsAddon(gtmEventUpgrade: string) {
    this.gtmService.pushTag({
      event: gtmEventUpgrade,
      gtm_trigger: 'true',
    });
    this.trackingService.trackEvent({
      event: gtmEventUpgrade,
    });

    this.paymentDataService.calculatedResponseSubscriber.pipe(first()).subscribe({
      next: value => {
        if (value?.isCompletePaymentState) {
          return;
        }
        this.eventEmitterService.openPaymentSiteDetails.next(PaymentStep.SELECT_PLAN);
      },
    });

    const siteRequestData = {
      id: this.currentSite.id,
      url: this.currentSite.url,
    };
    this.resetAdditionalSitesList();

    return this.paymentDataService.setPaymentData(
      ProductType.UsageStats,
      [siteRequestData],
      SubscriptionBillingCycleType.MONTHLY,
    );
  }

  hasIdMismatch(site: SiteSearchEntryDto): boolean {
    return site.id !== site.primarySiteId;
  }

  resetSiteData() {
    this.sitesManager.resetSiteData();
    this.isImgAltsUpgrade.set(false);
    this.showPayment.set(false);
  }

  calculatePrice(response: SiteExisting[], callback: () => void = () => {}): Promise<StripeProduct[]> {
    const sites: MultiSite[] = response.map(site => {
      return {id: site.id, url: site.url};
    });
    this.calculatedResponseSubscription = this.paymentDataService.calculatedResponseSubscriber.pipe(first()).subscribe({
      next: value => {
        if (value?.isCompletePaymentState || !value?.isUpdatePlans) {
          return;
        }
        callback();
      },
    });
    return this.paymentDataService.setPaymentData(
      ProductType.Remediation,
      sites,
      SubscriptionBillingCycleType.ANNUAL,
      true,
    );
  }

  private resetAllData() {
    this.resetSiteData();
    this.accountInfo = null;
    this.sitesManager.reset();
  }

  exportUsageStatsInCsv(
    exportStatsParams: IUsageStatsExportCSVRequest,
    callbacks: {onDone?: () => void; onRequestBlocked?: () => void} = {},
  ) {
    const {onDone = () => {}, onRequestBlocked = () => {}} = callbacks;

    return this.settingsApiService
      .exportUsageStatsInCsv(exportStatsParams)
      .pipe(tap(data => this.handleResponse(data, onDone, onRequestBlocked)));
  }

  private handleResponse(
    data: UwV2DashboardProductsUsageStatsExportCsvListData,
    onDone: () => void,
    onRequestBlocked: () => void,
  ) {
    const responseCode = data?.payload.code;

    if (responseCode === 200) {
      onDone();
    } else if ([429, 403].includes(responseCode)) {
      onRequestBlocked();
    } else {
      this.toastr.warning('Something went wrong, please try again in a few minutes');
    }
  }
}
